import { WizCommand, WizRoot } from '@wizeoo/akm-component-lib';
import { CrossVisionObj, DataPack, DataPackSet, FreeData, FreeDataValue } from '@wizeoo/akm-message-lib';
import { CompositeSourcePack } from '../core/sync/sync.service';
import { FreedataValueVO } from  '../../vo/core/freedatavalue.vo';
import { UpdateEntry } from '../core/sync/sync.service';
import { BaselineEngine, EM_ACTION } from '@wizeoo/akm-compute-lib';
import { clone } from 'lodash-es';
import { ExternalAPI } from '../../model/external-api';
import { Model } from '../../model/model';

/**
 * Appels du service de commit
 * Author LMA
 */
export class DataCommitCmd extends WizCommand<DataPack> {
  
  protected model: Model = null;

  constructor(model: Model) {
    super();
    this.model = model;
  }


  public sourceComposite: CompositeSourcePack = null; // le pack d'updates, pour éventuel rollback
  public sourcePack: DataPack = null; // les packs transmis au service
  public viewCallback: Function = null; // callback éventuelle pour appel depuis la vue Freedata

  public forceNoSilent: boolean = false;

  /** update */
  public commit(csp: CompositeSourcePack, callback: Function, viewCallback?: Function ): DataCommitCmd {
    this.sourceComposite = csp;
    this.sourcePack = this.buildPack(csp);
    this.sourcePack.originUuid = WizRoot.getInstance().uuid; // prise de l'origine unique
    this.viewCallback = viewCallback;
    this.configureRemote(ExternalAPI.DATA_COMMIT_API, this.sourcePack, callback);

    // console.log('datacommit', csp.updates[0]?.newValue, csp.updates[0]?.oldValue)

    return this;
  }

  /** Execution call
   * @override pour persistence locale des EMs (pour replay sur échec)
   */
  public async execute(): Promise<any> {
    super.execute();
  }

  /**
   * collecte du DataPackSet et création si nécessaire
   * Va alimenter le numRev pour le couple level#scen
   * @param source DataPack en cours de création
   * @param level 
   * @param scen 
   * @returns le DataPackSet
   */
  private getDataPackSet(source: DataPack, level: number, scen: string): DataPackSet {
    let result: DataPackSet;
    if(!source.datas) {
      source.datas = {}; 
    }
    if(!source.datas[level]) {
      source.datas[level] = {}; 
    }
    if(!source.datas[level][scen]) {
      
      let proto: DataPackSet = new DataPackSet();
      proto.level = level;
      proto.scenario = scen;
      proto.revNumber = this.model.widgetDataService.getDataloadByKey(source.visionCode, level, scen)?.revisionNumber || -1; // prise du n° de revision sur le dataload
      source.datas[level][scen] = proto; 
    }

    result = source.datas[level][scen];
    return result;

  }

  /** construction du pack remote */
  private buildPack(csp: CompositeSourcePack): DataPack {
 
    let result: DataPack = new DataPack();

    result.domain = this.model.domainCode;
    result.businessUnitCode = this.model.businessUnitCode;
    result.visionCode = this.model.visionCode;
    result.login = this.model.connectedUser.mail;

    /* ################################################
        gestion des freedatas
      ################################################ */

    // numRev pour les FDs
    result.freedataRevisionNumber = this.model.freedataSyncService.freedataLoad.revisionNumber;
    result.manageFreedata = !!csp.freedataManagement;

    if(!!csp.freedata) { // mise à jour du freedata
      let source: FreeData = clone(csp.freedata.source);
      source.freeDataValues = {}; // on rince les FDVs pour ne pas les écraser
      // csp.freedata.source.freeDataValues = {}; 
      result.inFreedata = source;
      if(!!csp.freedata.updateMap) { // et / ou de ses FDVs
        result.inFreedataValues = [];
        [...csp.freedata.updateMap.values()].forEach((pair: [FreedataValueVO, string])=> {
          const fdv: FreeDataValue = pair[0].source;
          fdv.operation = pair[1];
          result.inFreedataValues.push(fdv);
        });
      }
    }

    /* ################################################
        gestion des baselines & values
      ################################################ */

    let dataset: DataPackSet; // le set pour ajout des baselines / values par level / scen

    // contrôle de cohérence en cas de demande multiples de modifications sur les filterKeys
    // on va tout reporter sur le même clone
    let unicityCheck: Map<number, UpdateEntry> = new Map();

    // pack de modif cross-vision si nécessaire
    let cvos: CrossVisionObj[] = null;

    if(csp.updates?.length > 0) {
      
      csp.updates.forEach((update: UpdateEntry) => {
        // doublon sur mise à jour des filterKeys
        let twinError: boolean = false;

        if(!!update.baselineOperation) { // une opération sur la baseline  
          update.baseline.operation = update.baselineOperation;
          if(update.baseline.operation === EM_ACTION.CREATE) {// patch, on supprime trios et values pour création
            [...Object.values(update.baseline.values || {})].forEach(blv => {
              blv.revisionNumber = -1;
              blv.version = 0;
            })
            update.baseline.trios = null;
          } 

          // en cas de suppression en cascade, alimenter les steps enfants cibles
          // item en cas d'update en cascade
          if(
            (update.baseline.operation === EM_ACTION.DELETE && update.baseline.deleteCascade) ||
            (update.baseline.operation === EM_ACTION.UPDATE && update.baseline.updateCascade) ) {
            this.forceNoSilent = true; // on met un lock
              let idx: number = update.baseline.level;
            while(idx < update.baseline.cascadeDeep) {
              idx++;
              let subDataset: DataPackSet = this.getDataPackSet(result, idx, update.baseline.scen);
              
            }
          } 

          if(update.baseline.operation === EM_ACTION.UPDATE) { // dédoublonnage des demandes de modifs sur les filterKeys d'une même baseline
            if(unicityCheck.has(update.baseline.id)) { // déjà dedans mise à jour multiple des FilterKeys
              let realUpdate = unicityCheck.get(update.baseline.id);
              // et on applique la modif sur le 1er clone, c'est fini
              BaselineEngine.baselineSetFilterKeyValue(realUpdate.baseline, update.field, update.newValue);
              // console.log('massUpd', realUpdate.baseline);
              twinError = true;
            } else {
              unicityCheck.set(update.baseline.id, update);
            }
          }

          if(!twinError) {
            // prise du dataSet et ajout
            dataset = this.getDataPackSet(result, update.baseline.level, update.baseline.scen);
            if(!dataset.baselines) {
              dataset.baselines = [];
            }
            dataset.baselines.push(update.baseline);
          }

        }

        if(!!update.baselineValueOperation) { // une opération sur la baselineValue
          dataset = this.getDataPackSet(result, update.baseline.level, update.baseline.scen);
          update.baselineValue.operation = update.baselineValueOperation;
          if(!dataset.baselineValues) {
            dataset.baselineValues = [];
          }
          dataset.baselineValues.push(update.baselineValue);
        }

        // gestion des liens cross-vision
        if(!!update.crossVision) {
          if(!cvos) {
            cvos = [];
          }
          cvos.push(update.crossVision);
        }

      });
    }

    result.cvos = cvos;

    return result;
  }


  /**
   * utilisation du lock, ou pas (pas de lock si silent = true)
   * Attention, écrasé par commit (qui fait un configure), donc l'appeler APRES
   */
  public silent(silent: boolean = true): DataCommitCmd {
    this.doLock = !silent || this.forceNoSilent;
    return this;
  }

  /** sur retour, traitement selon la méthode */
  public wizResult() {
    this.sourcePack = this.result;
    super.wizResult();
  }
}
